import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { t as tRaw } from "../i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const AppCtx = createContext(null);

const USER_KEY = "scl_kindness_user";
const CURRENT_CHALLENGE_KEY = "scl_current_challenge";
const LANG_KEY = "scl_lang";

function readInitialUrlState() {
  if (typeof window === "undefined") return { pathParam: null, langParam: null, utmContent: "", utmSource: "", utmCampaign: "", utmMedium: "" };
  const p = new URLSearchParams(window.location.search);
  const rawPath = p.get("path");
  const normalized = rawPath === "activity" ? "spinner" : rawPath;
  const pathParam = normalized === "kindness" || normalized === "spinner" ? normalized : null;
  const langParam = p.get("lang");
  return {
    pathParam,
    langParam: langParam === "es" || langParam === "en" ? langParam : null,
    utmContent:  p.get("utm_content")  || "",
    utmSource:   p.get("utm_source")   || "",
    utmCampaign: p.get("utm_campaign") || "",
    utmMedium:   p.get("utm_medium")   || "",
  };
}

const URL_STATE = readInitialUrlState();

function initialLang() {
  if (URL_STATE.langParam) return URL_STATE.langParam;
  return localStorage.getItem(LANG_KEY);
}

function initialScreen(user, lang, pathParam) {
  if (!lang) return "language";
  // Spinner path is a public discovery tool — no signup required
  if (pathParam === "spinner") return "activities";
  // Direct kindness link: send new visitors straight into signup, returning users to gallery
  if (pathParam === "kindness") return user ? "gallery" : "welcome";
  // Bare URL (no ?path=): show the two-path home so casual scanners can self-select
  return "home";
}

export function AppProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { const raw = localStorage.getItem(USER_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
  });
  const [lang, setLangState] = useState(initialLang);
  const [pathParam, setPathParam] = useState(URL_STATE.pathParam);
  const utmContent = URL_STATE.utmContent;
  const utmSource = URL_STATE.utmSource;
  const utmCampaign = URL_STATE.utmCampaign;
  const utmMedium = URL_STATE.utmMedium;
  const [screen, setScreen] = useState(() => initialScreen(user, lang, URL_STATE.pathParam));
  const [currentChallenge, setCurrentChallenge] = useState(() => {
    try { const raw = localStorage.getItem(CURRENT_CHALLENGE_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
  });
  const [detailChallenge, setDetailChallenge] = useState(null);

  const setLang = useCallback((l) => {
    setLangState(l);
    localStorage.setItem(LANG_KEY, l);
  }, []);

  useEffect(() => { if (user) localStorage.setItem(USER_KEY, JSON.stringify(user)); }, [user]);
  useEffect(() => {
    if (currentChallenge) localStorage.setItem(CURRENT_CHALLENGE_KEY, JSON.stringify(currentChallenge));
    else localStorage.removeItem(CURRENT_CHALLENGE_KEY);
  }, [currentChallenge]);

  // sourcePath = path context for lead-form + CTA UTM
  // Priority: URL param -> current screen
  const sourcePath = useMemo(() => {
    if (pathParam === "kindness" || pathParam === "spinner") return pathParam;
    if (["activities"].includes(screen)) return "spinner";
    if (["gallery", "detail", "submit", "mychallenges", "leaderboard"].includes(screen)) return "kindness";
    return null;
  }, [pathParam, screen]);

  const createUser = useCallback(async (payload) => {
    const res = await axios.post(`${API}/users`, {
      ...payload,
      source_path: sourcePath || pathParam || "chooser",
      language: lang || "en",
      utm_content: utmContent || "",
    });
    setUser(res.data);
    return res.data;
  }, [sourcePath, pathParam, lang, utmContent]);

  const submitChallenge = useCallback(async ({ challenge, media_url, media_type, caption }) => {
    if (!user) throw new Error("no user");
    const res = await axios.post(`${API}/submissions`, {
      user_id: user.id,
      challenge_id: challenge.id,
      challenge_title: challenge.title,
      points: challenge.points,
      caption: caption || null,
      media_url: media_url || null,
      media_type: media_type || null,
    });
    setUser((u) => u ? { ...u, points: (u.points || 0) + challenge.points } : u);
    return res.data;
  }, [user]);

  const resetUser = useCallback(() => {
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(CURRENT_CHALLENGE_KEY);
    setUser(null);
    setCurrentChallenge(null);
    setDetailChallenge(null);
    // Bounce back to the two-path Home so casual visitors can self-select
    setScreen(lang ? "home" : "language");
  }, [lang]);

  const t = useCallback((key, vars) => tRaw(key, lang || "en", vars), [lang]);

  const value = useMemo(() => ({
    user, setUser, createUser,
    screen, setScreen,
    currentChallenge, setCurrentChallenge,
    detailChallenge, setDetailChallenge,
    submitChallenge, resetUser,
    lang: lang || "en", setLang,
    hasLangChoice: !!lang,
    pathParam, setPathParam,
    sourcePath, utmContent, utmSource, utmCampaign, utmMedium,
    t,
  }), [user, screen, currentChallenge, detailChallenge, createUser, submitChallenge, resetUser, lang, setLang, pathParam, sourcePath, utmContent, utmSource, utmCampaign, utmMedium, t]);

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}

export function useApp() {
  const ctx = useContext(AppCtx);
  if (!ctx) throw new Error("useApp must be within AppProvider");
  return ctx;
}
